/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React, { useMemo } from 'react';
import { ReactChildren, useAutoControlled } from '../..';
import { Checkbox, Icon } from '../../atoms';
import { CheckboxTableCell, RegularTableCell } from './dataListCell';
import { DataListRow } from './dataListRow';
import { Column, DataListBodyProps, Group } from './types';

const EMPTY_GROUPS: Array<Group> = [];

export function DataListBody<TItem, TData>({
    items,
    columns,
    groups = EMPTY_GROUPS,
    onGroupClicked,
    selected,
    checked,
    disabled,
    hideCheckBoxes,
    onSelect,
    onChecked,
    onItemChecked: onSelectionChanged,
    tableId,
}: DataListBodyProps<TItem, TData>): JSX.Element {
    function handleCheck(ev: React.ChangeEvent<HTMLInputElement>, item: TItem) {
        if (onChecked) {
            const newChecked = [...checked];
            if (ev.target.checked) {
                newChecked.push(item);
            } else {
                let itemIndex = -1;
                for (let i = 0; i < checked.length; i++) {
                    if (checked[i] === item) {
                        itemIndex = i;
                        break;
                    }
                }
                if (itemIndex !== -1) {
                    newChecked.splice(itemIndex, 1);
                }
            }
            onChecked(newChecked);
        }
        if (onSelectionChanged) {
            onSelectionChanged(ev, item);
        }
    }

    function isRowDisabled(item: TItem) {
        return disabled.some(disabledItem => {
            return disabledItem === item;
        });
    }

    function handleSelect(item: TItem) {
        if (onSelect && !isRowDisabled(item)) {
            onSelect(item);
        }
    }

    function renderRows(item: TItem, index: number) {
        return (
            <DataListRow
                key={index}
                isSelected={selected === item}
                isDisabled={isRowDisabled(item)}
                onSelect={() => handleSelect(item)}
            >
                {!hideCheckBoxes && (
                    <CheckboxTableCell>
                        <Checkbox
                            id={`data-list-${tableId}-row-${index}`}
                            checked={checked.includes(item)}
                            onChange={event => handleCheck(event, item)}
                        />
                    </CheckboxTableCell>
                )}
                <>
                    {columns.map((column, idx) => {
                        const fieldValue =
                            column.fieldName in item
                                ? item[column.fieldName]
                                : '';
                        const cellValue = column.renderCell
                            ? column.renderCell(item, idx, column)
                            : fieldValue;
                        return (
                            <RegularTableCell
                                key={idx}
                                onClickCapture={() => {
                                    handleSelect(item);
                                }}
                                onClick={ev => {
                                    if (column?.onColumnClick) {
                                        column.onColumnClick(ev, column);
                                    }
                                }}
                                onContextMenu={ev => {
                                    if (column?.onColumnContextMenu) {
                                        column.onColumnClick(ev, column);
                                        ev.preventDefault();
                                    }
                                }}
                                style={column.cellStyle}
                                aria-label={column.ariaLabel}
                                title={
                                    column.resolveCellTitle?.(item) ||
                                    column.ariaLabel
                                }
                            >
                                {cellValue}
                            </RegularTableCell>
                        );
                    })}
                </>
            </DataListRow>
        );
    }

    const startIdxToGroups: Map<number, Group> = useMemo(
        () => new Map(groups.map(g => [g.startIndex, g])),
        [groups],
    );

    return groups.length > 0 ? (
        <>
            {groups.map((group, index) => {
                const foundGroupRow = startIdxToGroups.get(group.startIndex);
                if (!foundGroupRow) {
                    return null;
                }

                const children = items
                    .slice(group.startIndex, group.startIndex + group.count)
                    .map(renderRows);
                return (
                    <DataListGroup
                        key={index}
                        group={foundGroupRow}
                        hideCheckBoxes={hideCheckBoxes}
                        columns={columns}
                        onGroupClicked={onGroupClicked}
                    >
                        {children}
                    </DataListGroup>
                );
            })}
        </>
    ) : (
        <>{items.map(renderRows)}</>
    );
}

interface DataListGroupProps<TItem, TData> {
    group?: Group;
    hideCheckBoxes?: boolean;
    columns: Array<Column<TItem, TData>>;
    onGroupClicked?: (
        group: Group,
        ev?: React.MouseEvent<HTMLElement, MouseEvent>,
    ) => void;
    children: ReactChildren;
}

function DataListGroup<TItem, TData>({
    group,
    hideCheckBoxes,
    columns,
    onGroupClicked,
    children,
}: DataListGroupProps<TItem, TData>): JSX.Element {
    const [collapsed, setCollapsed] = useAutoControlled({
        initialValue: false,
        defaultValue: group.defaultCollapsed,
        value: group.isCollapsed,
    });

    return (
        <>
            <DataListRow>
                <RegularTableCell
                    colSpan={
                        hideCheckBoxes ? columns.length : columns.length + 1
                    }
                    onClick={ev => {
                        setCollapsed(!collapsed);
                        onGroupClicked?.(group, ev);
                    }}
                    style={{ cursor: 'pointer' }}
                >
                    <Icon iconName={collapsed ? 'down' : 'up'} />
                    <b style={{ marginLeft: '0.75rem' }}>
                        {typeof group.name === 'string'
                            ? `${group.name} (${group.count})`
                            : group.name}
                    </b>
                </RegularTableCell>
            </DataListRow>
            {!collapsed && children}
        </>
    );
}
